import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Observable, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { OrganizationDto } from '../../../../../modules/organizations/dtos/organization';
import { OrganizationsService } from '../../../../../modules/organizations/organizations.service';
import { PaginationArgs } from '../../../../../shared/components/pagination/models/pagination-args.model';
import { SnackBarService } from '../../../../../shared/components/snack-bar/snack-bar.service';
import { SortingType } from '../../../../../shared/enums/sorting-type.enum';
import { PartnerRegistration } from '../../../models/organizations.model';
import { OrganizationsLegacyService } from '../../../organizations-legacy.service';

interface DialogData {
	mode: 'link' | 'unlink';
	partner: PartnerRegistration;
}

@Component({
	selector: 'app-link-organizations-modal',
	styleUrls: ['./link-organizations-modal.component.scss'],
	templateUrl: './link-organizations-modal.component.html',
})
export class LinkOrganizationsModalComponent implements OnInit {
	availableOrganizations$: Observable<OrganizationDto[]> = of([]);
	isLoading = true;
	organizationControl = new FormControl(null, [Validators.required]);

	constructor(
		public dialogRef: MatDialogRef<LinkOrganizationsModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DialogData,
		private readonly organizationsService: OrganizationsService,
		private readonly legacyOrgService: OrganizationsLegacyService,
		private readonly snackBarService: SnackBarService,
	) {}

	closeModal(): void {
		this.dialogRef.close();
	}

	ngOnInit(): void {
		this.isLoading = true;

		const select = JSON.stringify({
			id: true,
			name: true,
			name_secondary: true,
		});

		const where = {
			is_matriz: true,
			organizationsPartners: {
				some: {
					deletedAt: null,
					partnerId: this.data.partner.id,
				},
			},
		};

		const orderBy = JSON.stringify({ name_secondary: SortingType.ASCENDING });

		this.organizationsService
			.findMany(
				new PaginationArgs({ limit: 150, orderBy, page: 1, select, where }),
			)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(() => {
				if (this.data.mode === 'link') {
					const whereClause = {
						is_matriz: true,
						organizationBrokerLink: null,
					};
					this.availableOrganizations$ = this.organizationsService
						.findMany(
							new PaginationArgs({
								limit: 150,
								orderBy,
								page: 1,
								select,
								where: whereClause,
							}),
						)
						.pipe(map((response) => response.result));
				} else {
					const whereClause = {
						is_matriz: true,
						organizationBrokerLink: {
							deletedAt: null,
							partnerBrokerId: this.data.partner.id,
						},
					};

					this.availableOrganizations$ = this.legacyOrgService
						.findOrganizationsByFilter(whereClause)
						.pipe(finalize(() => (this.isLoading = false)));
				}
			});
	}

	saveItems(): void {
		if (this.organizationControl.invalid) {
			return;
		}

		const selectedOrgId = this.organizationControl.value;
		const partnerBrokerId = this.data.partner.id;

		if (typeof selectedOrgId === 'string') {
			if (this.data.mode === 'link') {
				this.legacyOrgService
					.assignBrokerToOrganization(partnerBrokerId, selectedOrgId)
					.subscribe({
						error: (error) => {
							this.snackBarService.showSnack(
								error?.error?.message || 'Falha ao vincular organização.',
								'error',
							);
						},
						next: () => {
							const name = this.data?.partner?.name || '';
							const message =
								name && String(name).trim() !== ''
									? `Organização vinculada à corretora "${name}" com sucesso.`
									: 'Organização vinculada à corretora com sucesso.';
							this.snackBarService.showSnack(message, 'success');
							this.dialogRef.close(true);
						},
					});
			} else {
				this.legacyOrgService
					.unassignBrokerFromOrganization(selectedOrgId)
					.subscribe({
						error: (error) => {
							this.snackBarService.showSnack(
								error?.error?.message || 'Falha ao desvincular organização.',
								'error',
							);
						},
						next: () => {
							const name = this.data?.partner?.name || '';
							const message =
								name && String(name).trim() !== ''
									? `Organização desvinculada da corretora "${name}" com sucesso.`
									: 'Organização desvinculada da corretora com sucesso.';
							this.snackBarService.showSnack(message, 'success');
							this.dialogRef.close(true);
						},
					});
			}
		}
	}

	trackByOrgId(
		index: number,
		org: { id: number | string } | null,
	): number | string | null {
		return org ? org.id : null;
	}
}
